/**
 * Client-side fallback for a confirmed Shopify platform bug: on non-default
 * storefront locale/market URLs (e.g. /en/, /en-es/), Liquid's
 * `product.metafields...` accessor returns blank for `number_decimal` and
 * `json` type metafields (verified: `number_integer` fields render fine,
 * `number_decimal`/`json` don't — same product, same request, confirmed
 * with real data via the Admin API and reported to Shopify support).
 *
 * The nutrition table, feeding-guide table, and duration calculator all
 * always render their full skeleton server-side (see product-block.liquid,
 * product-feeding-guide-table.liquid, product-duration-calculator.liquid) —
 * this script only checks whether that skeleton came back empty and, if so,
 * re-fetches the same data via the Storefront API (unaffected by the bug)
 * and fills it in.
 */
(function () {
  function isPlaceholder(text) {
    const trimmed = (text || '').trim();
    return trimmed === '' || trimmed === '-';
  }

  // Carbohydrates and moisture are frequently calculated/estimated rather
  // than lab-measured (see snippets/product-block.liquid's server-rendered
  // "Calculated value. How?" link) — when this JS fallback has to rebuild the
  // cell from scratch (locale bug path), it must append the same link. Kcal
  // is deliberately NOT included: no per-product flag distinguishes a
  // declared kcal value from an Atwater-estimated one, so linking it
  // unconditionally would be a false "calculated" claim on many products.
  const CALCULATED_VALUE_ANCHORS = {
    'nutrition.hidratos_carbono': '#carbohydrates-explainer',
    'nutrition.humidade': '#moisture-explainer',
  };

  // Returns whether at least one cell actually had real data (either already server-rendered,
  // or filled here from the API) — callers use this to decide whether the nutrition column
  // should be shown at all (see updateAnalyticsFeedingRowLayout).
  function fillNutritionCells(root, productMetafields) {
    let hasAnyValue = false;
    root.querySelectorAll('[data-metafield-cell]').forEach((cell) => {
      if (!isPlaceholder(cell.textContent)) {
        hasAnyValue = true;
        return;
      }

      const key = cell.dataset.metafieldCell;
      const isPercent = cell.dataset.metafieldIsPercent === 'true';
      const value = productMetafields[key];
      if (value == null || value === '') return;

      let html = isPercent ? `${value}%` : value;
      const anchor = CALCULATED_VALUE_ANCHORS[key];
      if (anchor && window.strings.product.nutrition && window.storefrontApiConfig) {
        const label = window.strings.product.nutrition.calculatedValueHow;
        const ourDataUrl = window.theme && window.theme.ourDataPageUrl;
        if (label && ourDataUrl) {
          html += ` <a href="${ourDataUrl}${anchor}" class="calculated-value-link">${label}</a>`;
        }
      }
      cell.innerHTML = html;
      hasAnyValue = true;
    });
    return hasAnyValue;
  }

  // Returns whether the feeding-guide table ended up with real rows (either already
  // server-rendered, or filled here from the API).
  function fillFeedingGuideRows(root, feedingGuideJson) {
    const tbody = root.querySelector('[data-feeding-guide-rows]');
    if (!tbody) return false;
    if (tbody.children.length) return true; // already has rows

    let rows;
    try {
      rows = JSON.parse(feedingGuideJson);
    } catch (error) {
      return false;
    }
    if (!Array.isArray(rows) || !rows.length) return false;

    const strings = window.strings.product.feedingGuide;
    tbody.innerHTML = rows
      .map((row) => {
        const activity = row.conditions || strings.activityNormal;
        const label = row.label || row.weight;
        return `<tr><th class="rte text-left">${label}</th><td class="rte text-left">${activity}</td><td class="rte metafield-csv-lists text-left">${row.dose}</td></tr>`;
      })
      .join('');
    return true;
  }

  function buildFaqItemsHtml(blockId, faqItems) {
    return faqItems
      .map((item, index) => {
        const question = escapeHtml(item.question || '');
        const answer = escapeHtml(item.answer || '');
        return `
          <custom-accordion class="product__accordion accordion product-faq-item">
            <details id="Details-${blockId}-${index + 1}-js">
              <summary>
                <div class="summary__title">
                  <h3 class="accordion__title inline-richtext font-medium text-body t4">${question}</h3>
                </div>
              </summary>
              <div class="custom-accordion__panel custom-scrollbar">
                <div class="flex">
                  <div class="accordion__content rte product-faq-item__answer">
                    <p class="t4">${answer}</p>
                  </div>
                </div>
              </div>
            </details>
          </custom-accordion>
        `;
      })
      .join('');
  }

  function escapeHtml(text) {
    const div = document.createElement('div');
    div.textContent = text;
    return div.innerHTML;
  }

  function buildFaqSchema(faqItems) {
    return {
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: faqItems.map((item) => ({
        '@type': 'Question',
        name: item.question || '',
        acceptedAnswer: {
          '@type': 'Answer',
          text: item.answer || '',
        },
      })),
    };
  }

  function fillFaqAccordion(root, faqJson) {
    let faqItems;
    try {
      faqItems = JSON.parse(faqJson);
    } catch (error) {
      faqItems = null;
    }

    if (!Array.isArray(faqItems) || !faqItems.length) {
      // Genuinely no FAQ data (not a locale-bug false-blank) — remove the
      // hidden marker entirely so the DOM/view-source matches "nothing
      // rendered", same as if the metafield had never existed.
      root.remove();
      return;
    }

    const blockId = root.dataset.faqBlockId || 'js-fallback';
    const heading = escapeHtml(root.dataset.faqHeading || '');
    const headingTag = root.dataset.faqHeadingTag || 'h2';
    const headingSize = root.dataset.faqHeadingSize || 't2';

    root.innerHTML = `
      <custom-accordion class="product__accordion accordion">
        <details id="Details-${blockId}-js">
          <summary>
            <div class="summary__title">
              <${headingTag} class="accordion__title inline-richtext font-bold text-body ${headingSize}">${heading}</${headingTag}>
            </div>
          </summary>
          <div class="custom-accordion__panel custom-scrollbar">
            <div class="flex">
              <div class="accordion__content rte" data-faq-accordion-items>
                ${buildFaqItemsHtml(blockId, faqItems)}
              </div>
            </div>
          </div>
        </details>
      </custom-accordion>
    `;
    root.style.display = '';

    if (root.dataset.faqRenderSchema === 'true') {
      const script = document.createElement('script');
      script.type = 'application/ld+json';
      script.setAttribute('data-faq-schema', '');
      script.textContent = JSON.stringify(buildFaqSchema(faqItems));
      root.appendChild(script);
    }
  }

  function fillCalculatorConditions(selectEl, distinctConditions) {
    const strings = window.strings.product.feedingGuide;
    selectEl.innerHTML = distinctConditions
      .map((condition) => {
        const label = condition || strings.activityNormal;
        const value = condition || '';
        return `<option value="${value}">${label}</option>`;
      })
      .join('');
  }

  function fillCalculator(root, feedingGuideJson, variantsById) {
    const calculator = root.querySelector('product-duration-calculator');
    if (!calculator || typeof calculator.updateData !== 'function') return;

    let feedingGuide;
    try {
      feedingGuide = JSON.parse(feedingGuideJson);
    } catch (error) {
      return;
    }
    if (!Array.isArray(feedingGuide) || !feedingGuide.length) return;

    // packWeightKg was already resolved server-side (from the variant title,
    // since Shopify inventory-item weight is deliberately left at 0 on this
    // store) — that part isn't affected by the locale bug, only durationGuide
    // (json type) is, so read the existing packWeightKg back out.
    let existingData;
    try {
      existingData = JSON.parse(calculator.querySelector('.product-duration-calculator__data').textContent);
    } catch (error) {
      existingData = { durationGuideByVariant: {} };
    }

    const durationGuideByVariant = {};
    Object.keys(variantsById || {}).forEach((variantId) => {
      const durationGuideRaw = variantsById[variantId]['catalog.duration_guide'];
      const packWeightMatch = existingData.durationGuideByVariant[variantId];
      if (!durationGuideRaw || !packWeightMatch) return;

      try {
        durationGuideByVariant[variantId] = {
          durationGuide: JSON.parse(durationGuideRaw),
          packWeightKg: packWeightMatch.packWeightKg,
        };
      } catch (error) {
        // skip malformed variant entry
      }
    });

    calculator.updateData(feedingGuide, durationGuideByVariant);

    const selectEl = calculator.querySelector('.product-duration-calculator__condition-input');
    if (selectEl && selectEl.tagName === 'SELECT') {
      const distinctConditions = [...new Set(feedingGuide.map((row) => row.conditions))];
      fillCalculatorConditions(selectEl, distinctConditions);
    }
  }

  // Shared by every fallback fetch on this page — ProductMetafieldsFetcher
  // caches its response per product handle, keyed only by handle (not by
  // which identifiers were requested), so every caller for the same product
  // MUST request the same full identifier set or a later caller silently
  // gets an earlier, narrower cached response missing its fields.
  const PRODUCT_IDENTIFIERS = [
    { namespace: 'nutrition', key: 'proteina_bruta' },
    { namespace: 'nutrition', key: 'gordura_bruta' },
    { namespace: 'nutrition', key: 'fibra_bruta' },
    { namespace: 'nutrition', key: 'cinza_bruta' },
    { namespace: 'nutrition', key: 'humidade' },
    { namespace: 'nutrition', key: 'hidratos_carbono' },
    { namespace: 'nutrition', key: 'kcal' },
    { namespace: 'catalog', key: 'feeding_guide' },
    { namespace: 'catalog', key: 'faq' },
  ];
  const VARIANT_IDENTIFIERS = [{ namespace: 'catalog', key: 'duration_guide' }];

  async function run() {
    // Scoped to our own containers only — [data-product-handle] alone also
    // matches the theme's pre-existing attribute on the whole .product
    // section (sections/main-product.liquid).
    const roots = document.querySelectorAll(
      '.product-analytics-feeding-row[data-product-handle], .product-feeding-calculator-block[data-product-handle]'
    );

    // Updates the two-column row's layout after either column's real data state is confirmed
    // (locale-bug recovery or genuine emptiness) — mirrors the server-side
    // product-analytics-feeding-row--single class in product-block.liquid: full-width when
    // only one side has data, hidden entirely when neither does.
    function updateAnalyticsFeedingRowLayout(root, hasNutrition, hasFeedingGuide) {
      const nutritionCol = root.querySelector('.product-nutrition-table-col');
      const feedingGuideCol = root.querySelector('.product-feeding-guide-table-col');
      if (nutritionCol) nutritionCol.style.display = hasNutrition ? '' : 'none';
      if (feedingGuideCol) feedingGuideCol.style.display = hasFeedingGuide ? '' : 'none';
      root.classList.toggle('product-analytics-feeding-row--single', !hasNutrition || !hasFeedingGuide);
      root.style.display = !hasNutrition && !hasFeedingGuide ? 'none' : '';
    }

    await Promise.all(
      Array.from(roots).map(async (root) => {
        // Only the .product-analytics-feeding-row root carries these two server-computed
        // flags (.product-feeding-calculator-block doesn't) — undefined dataset reads become
        // the string "undefined", so check the attribute's actual presence instead.
        const hasNutritionAttr = root.dataset.hasNutrition;
        const hasFeedingGuideAttr = root.dataset.hasFeedingGuide;
        const serverSaysNutritionEmpty = hasNutritionAttr === 'false';
        const serverSaysFeedingGuideEmpty = hasFeedingGuideAttr === 'false';

        const nutritionBlank =
          serverSaysNutritionEmpty ||
          Array.from(root.querySelectorAll('[data-metafield-cell]')).some((cell) => isPlaceholder(cell.textContent));
        const feedingGuideTbody = root.querySelector('[data-feeding-guide-rows]');
        const feedingGuideBlank =
          serverSaysFeedingGuideEmpty || (feedingGuideTbody && !feedingGuideTbody.children.length);
        const calculatorScript = root.querySelector('.product-duration-calculator__data');
        const conditionSelect = root.querySelector('.product-duration-calculator__condition-input');
        // Only treat the calculator as broken when its <select> rendered with
        // zero <option>s — i.e. distinct_conditions was empty server-side,
        // meaning feeding_guide was genuinely blank. Never re-parse/replace
        // an already-working calculator based on the JSON script's content.
        const calculatorBlank = !!(calculatorScript && conditionSelect && conditionSelect.options.length === 0);

        if (!nutritionBlank && !feedingGuideBlank && !calculatorBlank) return;

        const productHandle = root.dataset.productHandle;
        const result = await window.ProductMetafieldsFetcher.fetchProductMetafields(
          productHandle,
          PRODUCT_IDENTIFIERS,
          VARIANT_IDENTIFIERS
        );
        if (!result) {
          // Two-column row (has hasNutrition/hasFeedingGuide attrs): can't confirm either side
          // via the API — leave the server's own best-effort layout as-is (don't blank out a
          // row that might genuinely have data just because the API call itself failed).
          return;
        }

        const isAnalyticsFeedingRow = hasNutritionAttr != null;
        let nutritionHasRealData = !nutritionBlank;
        let feedingGuideHasRealData = !feedingGuideBlank;

        if (nutritionBlank) {
          nutritionHasRealData = fillNutritionCells(root, result.product);
        }

        const feedingGuideJson = result.product['catalog.feeding_guide'];
        if (feedingGuideJson) {
          if (feedingGuideBlank) feedingGuideHasRealData = fillFeedingGuideRows(root, feedingGuideJson);
          if (calculatorBlank) fillCalculator(root, feedingGuideJson, result.variantsById);
        } else if (feedingGuideBlank) {
          feedingGuideHasRealData = false;
        }

        if (isAnalyticsFeedingRow) {
          updateAnalyticsFeedingRowLayout(root, nutritionHasRealData, feedingGuideHasRealData);
        }
      })
    );

    // FAQ block: always present as a (possibly hidden) marker regardless of
    // whether the server-side render had data — see the comment in
    // product-block.liquid's `faq` branch. Unconditionally verify via the
    // Storefront API whether real data exists, then either fill it in
    // (locale-bug false-blank, or genuinely first paint) or remove the
    // marker (product truly has no FAQ).
    const faqRoots = document.querySelectorAll('.product-faq-block[data-product-handle]');
    await Promise.all(
      Array.from(faqRoots).map(async (root) => {
        const hasRenderedItems = !!root.querySelector('[data-faq-accordion-items] .product-faq-item');
        if (hasRenderedItems) return; // already correctly rendered server-side

        const productHandle = root.dataset.productHandle;
        const result = await window.ProductMetafieldsFetcher.fetchProductMetafields(
          productHandle,
          PRODUCT_IDENTIFIERS,
          VARIANT_IDENTIFIERS
        );
        if (!result) {
          root.remove();
          return;
        }

        fillFaqAccordion(root, result.product['catalog.faq']);
      })
    );
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', run);
  } else {
    run();
  }
})();
